// Pure helpers for `listingVariants`, the author-owned map of extra listing
// creatives on an event doc. They live here rather than in the section component
// because the form hook writes the variant photo too: the async half of Generate
// and Upload belongs with the other Firebase work in useEventForm, and both
// sides have to agree on the shape of the map.
//
// Nothing in this file touches Firebase, so it stays cheap to test.

// "a" is reserved for the base listing and never appears in `listingVariants`,
// so the one extra creative the form offers is always "b". The data shape allows
// more; the form deliberately does not, because a second creative is already an
// occasional power feature.
export const VARIANT_ID = "b";

const OVERRIDE_FIELDS = ["name", "description", "photo"];

// A variant with no overrides resolves to content identical to the base and
// would publish a duplicate listing, so the server refuses to publish it. The
// author needs to know that in the form rather than by noticing nothing
// happened. A File or Blob in `photo` counts: it is a photo the author picked,
// it just has not been uploaded yet.
export const hasVariantOverride = (variant) =>
  OVERRIDE_FIELDS.some((field) => {
    const value = variant?.[field];
    if (!value) return false;
    return typeof value === "string" ? value.trim() !== "" : true;
  });

export const meetupListingUrl = (id) =>
  id ? `https://www.meetup.com/gist-irl/events/${id}/` : null;

export const eventbriteListingUrl = (id) =>
  id ? `https://www.eventbrite.com/e/${id}` : null;

/**
 * Set the variant's photo, replacing whatever was there.
 *
 * A variant has ONE hero image, not an album, so every route in (generate,
 * library, upload, a pasted URL) replaces rather than appends. Passing "" is how
 * the photo goes back to inheriting the event's own.
 *
 * Other variant ids in the map are carried through untouched.
 */
export const withVariantPhoto = (variants, photo, variantId = VARIANT_ID) => ({
  ...(variants || {}),
  [variantId]: { ...((variants || {})[variantId] || {}), photo },
});

/**
 * The copy this listing will actually show: its override where it has one, the
 * event's own value where the override is blank.
 *
 * Generating an image has to run on this rather than on the event, because a
 * variant exists precisely to make a different pitch. Generating from the base
 * copy would produce an image for the listing this one is meant to differ from.
 */
export const resolveVariantCopy = (variant, event) => ({
  name: (variant?.name || "").trim() || event?.name || "",
  description: (variant?.description || "").trim() || event?.description || "",
});
